/**
 * @module svg/logo
 *
 * The inmethods mark.
 *
 * ## The idea
 *
 * The mark is an economy trick: a single lowercase **i** dot, a single stem and
 * a single arch spell **"in"** — the first syllable of the name — because the
 * stem is shared by the *i* and the *n*. That continuous stroke is then wrapped
 * in an **orbit**: the method cycle that keeps turning around every engagement.
 * A lit node sits on the orbit — the point of intelligence the method is there
 * to place.
 *
 * Three ideas in one glyph: the word, the method, the AI.
 *
 * The mark is authored on a 48×48 grid and inherits no colour of its own except
 * its accents, so it can be re-tinted from CSS with `currentColor` when needed.
 */

import { escapeAttribute } from "./icons.js";

/**
 * Monotonic counter for unique gradient ids (multiple marks per page must not
 * share gradient ids or their gradients cross-talk).
 */
let uid = 0;

/**
 * @typedef {Object} LogoOptions
 * @property {number} [size=48]        Nominal viewBox size (square).
 * @property {string} [accent="#5fd4c4"] Primary accent — the stroke and the node.
 * @property {string} [secondary="#d8a657"] Secondary accent — the orbit.
 * @property {boolean} [animated=true]  Add classes/ids used by the GSAP timeline.
 * @property {string} [className=""]    Extra class names on the `<svg>`.
 * @property {string} [title="inmethods"] Accessible title.
 * @property {boolean} [decorative=false] When true the mark is `aria-hidden`.
 */

/**
 * Render the inmethods mark as an SVG string.
 *
 * @param {LogoOptions} [options]
 * @returns {string} SVG markup.
 */
export function logoMark(options = {}) {
  const {
    size = 48,
    accent = "#5fd4c4",
    secondary = "#d8a657",
    animated = true,
    className = "",
    title = "inmethods",
    decorative = false,
  } = options;

  uid += 1;
  const id = `im-logo-${uid}`;

  // Geometry (48×48 grid).
  const orbitCx = 24;
  const orbitCy = 25;
  const rx = 21;
  const ry = 20;
  const tilt = -22;

  // The "in": shared stem at x = 15.5, arch sweeping to x = 34.
  const stemX = 15.5;
  const baseY = 36.5;
  const shoulderY = 20.5;
  const archEndX = 34;
  const tittle = { cx: stemX, cy: 12.4, r: 2.7 };

  const a11y = decorative
    ? 'aria-hidden="true" focusable="false"'
    : `role="img" aria-label="${escapeAttribute(title)}"`;

  const orbitClass = animated ? 'class="logo__orbit"' : "";
  const strokeClass = animated ? 'class="logo__stroke"' : "";
  const nodeClass = animated ? 'class="logo__node"' : "";
  const tittleClass = animated ? 'class="logo__tittle"' : "";

  return `
<svg class="logo-mark ${className}" viewBox="0 0 48 48" width="${size}" height="${size}"
     fill="none" ${a11y}>
  <defs>
    <linearGradient id="${id}-stroke" x1="12" y1="38" x2="36" y2="14" gradientUnits="userSpaceOnUse">
      <stop offset="0%" stop-color="${secondary}"/>
      <stop offset="55%" stop-color="${accent}"/>
      <stop offset="100%" stop-color="${accent}"/>
    </linearGradient>
    <linearGradient id="${id}-orbit" x1="3" y1="6" x2="45" y2="44" gradientUnits="userSpaceOnUse">
      <stop offset="0%" stop-color="${secondary}" stop-opacity="0.95"/>
      <stop offset="60%" stop-color="${accent}" stop-opacity="0.55"/>
      <stop offset="100%" stop-color="${accent}" stop-opacity="0.12"/>
    </linearGradient>
    <radialGradient id="${id}-glow" cx="50%" cy="50%" r="50%">
      <stop offset="0%" stop-color="${accent}" stop-opacity="0.85"/>
      <stop offset="100%" stop-color="${accent}" stop-opacity="0"/>
    </radialGradient>
    <filter id="${id}-soft" x="-80%" y="-80%" width="260%" height="260%">
      <feGaussianBlur stdDeviation="1.6"/>
    </filter>
  </defs>

  <!-- Outer method orbit: a tilted ellipse, read as a cycle in 3D -->
  <g transform="rotate(${tilt} ${orbitCx} ${orbitCy})">
    <ellipse ${orbitClass} cx="${orbitCx}" cy="${orbitCy}" rx="${rx}" ry="${ry}"
             stroke="url(#${id}-orbit)" stroke-width="1.7"/>
    <ellipse cx="${orbitCx}" cy="${orbitCy}" rx="${rx - 3.4}" ry="${ry - 3.2}"
             stroke="${accent}" stroke-width="0.55" stroke-opacity="0.28"
             stroke-dasharray="1.6 5.4"/>
  </g>

  <!-- The "in": one continuous stroke — tittle, stem, arch -->
  <path ${strokeClass}
        d="M ${stemX} ${baseY} V ${shoulderY}
           C ${stemX} 22.6 19.4 19.6 24.4 20.2
           C 29.6 20.9 32.4 24.4 33.4 30.2
           L ${archEndX} ${baseY}"
        stroke="url(#${id}-stroke)" stroke-width="3.4"
        stroke-linecap="round" stroke-linejoin="round"/>
  <circle ${tittleClass} cx="${tittle.cx}" cy="${tittle.cy}" r="${tittle.r}"
          fill="${accent}"/>

  <!-- The lit node: the point of intelligence on the method cycle -->
  <g class="logo__node-group">
    <circle ${nodeClass} cx="40.6" cy="17.4" r="6.4" fill="url(#${id}-glow)" opacity="0.55"/>
    <circle ${nodeClass} cx="40.6" cy="17.4" r="2.5" fill="#ffffff"/>
  </g>
</svg>`.trim();
}

/**
 * Render the mark inside a soft rounded tile — used for the favicon, the social
 * card and anywhere the mark needs to hold its own against a background.
 *
 * @param {Object} [options]
 * @param {number} [options.size=64]
 * @param {string} [options.background="#0e0f11"]
 * @param {string} [options.accent="#5fd4c4"]
 * @param {string} [options.secondary="#d8a657"]
 * @returns {string} SVG markup.
 */
export function logoBadge(options = {}) {
  const { size = 64, background = "#0e0f11", accent = "#5fd4c4", secondary = "#d8a657" } = options;

  uid += 1;
  const id = `im-badge-${uid}`;

  return `
<svg class="logo-badge" viewBox="0 0 64 64" width="${size}" height="${size}"
     fill="none" role="img" aria-label="inmethods">
  <defs>
    <linearGradient id="${id}-bg" x1="0" y1="0" x2="64" y2="64" gradientUnits="userSpaceOnUse">
      <stop offset="0%" stop-color="#15171a"/>
      <stop offset="100%" stop-color="${background}"/>
    </linearGradient>
    <linearGradient id="${id}-edge" x1="0" y1="0" x2="64" y2="64" gradientUnits="userSpaceOnUse">
      <stop offset="0%" stop-color="${secondary}" stop-opacity="0.5"/>
      <stop offset="50%" stop-color="${accent}" stop-opacity="0.35"/>
      <stop offset="100%" stop-color="${accent}" stop-opacity="0.08"/>
    </linearGradient>
  </defs>
  <rect x="1" y="1" width="62" height="62" rx="17" fill="url(#${id}-bg)"
        stroke="url(#${id}-edge)" stroke-width="1.4"/>
  <g transform="translate(8 8) scale(1)">
    ${logoMark({ size: 48, accent, secondary, animated: false, decorative: true })}
  </g>
</svg>`.trim();
}

/**
 * The full lockup: mark plus wordmark, as markup. Used in the header and footer
 * where the mark and text must sit on one baseline.
 *
 * @param {Object} [options]
 * @param {"header"|"footer"} [options.variant="header"]
 * @param {number} [options.size=34]
 * @returns {string} Markup for the lockup's inner content.
 */
export function logoLockup(options = {}) {
  const { variant = "header", size = 34 } = options;
  const className = variant === "footer" ? "logo logo--footer" : "logo";

  return `
<span class="${className}">
  <span class="logo__mark" aria-hidden="true">${logoMark({ size, animated: false, decorative: true })}</span>
  <span class="logo__word">in<span class="logo__word-accent">methods</span><span class="logo__word-dot" aria-hidden="true">.</span></span>
</span>`.trim();
}
